import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Leaf } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <nav className="bg-card border-b border-border sticky top-0 z-50 shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="navbar-logo">
              <img src="/logo.png" alt="OrgShop" className="h-11 md:h-14 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/products" className="text-foreground hover:text-primary font-medium transition-colors" data-testid="nav-products-link">
                Products
              </Link>
              <Link to="/become-vendor" className="text-foreground hover:text-primary font-medium transition-colors" data-testid="nav-sell-link">
                Sell on OrgShop
              </Link>
              <Link to="/cart" className="relative mr-2" data-testid="nav-cart-link">
                <ShoppingCart className="w-6 h-6 text-foreground hover:text-primary transition-colors" />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                    data-testid="nav-cart-count"
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
              
              {user ? (
                <div className="flex items-center space-x-4">
                  {(user.role === "admin" || user.role === "vendor") && (
                    <Link
                      to={user.role === "admin" ? "/admin" : "/vendor"}
                      className="text-foreground hover:text-primary font-medium transition-colors"
                      data-testid="nav-dashboard-link"
                    >
                      Dashboard
                    </Link>
                  )}
                  <Link
                    to="/dashboard"
                    title="My Account"
                    className="text-foreground hover:text-primary transition-colors"
                    data-testid="nav-account-link"
                  >
                    <User className="w-6 h-6" />
                  </Link>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="text-foreground hover:text-primary"
                    data-testid="nav-logout-btn"
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
                  data-testid="nav-login-btn"
                >
                  Login
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
              <div className="flex flex-col space-y-4">
                <Link to="/products" className="text-foreground hover:text-primary font-medium" data-testid="mobile-products-link">
                  Products
                </Link>
                <Link to="/become-vendor" className="text-foreground hover:text-primary font-medium" data-testid="mobile-sell-link">
                  Sell on OrgShop
                </Link>
                <Link to="/cart" className="text-foreground hover:text-primary font-medium" data-testid="mobile-cart-link">
                  Cart
                </Link>
                {user ? (
                  <>
                    {(user.role === "admin" || user.role === "vendor") && (
                      <Link
                        to={user.role === "admin" ? "/admin" : "/vendor"}
                        className="text-foreground hover:text-primary font-medium"
                        data-testid="mobile-dashboard-link"
                      >
                        Dashboard
                      </Link>
                    )}
                    <Link to="/dashboard" className="text-foreground hover:text-primary font-medium flex items-center gap-2" data-testid="mobile-account-link">
                      <User className="w-5 h-5" /> My Account
                    </Link>
                    <Button onClick={handleLogout} variant="ghost" className="justify-start" data-testid="mobile-logout-btn">
                      Logout
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setShowAuthModal(true)} className="bg-primary text-primary-foreground" data-testid="mobile-login-btn">
                    Login
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}
